import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const puntajeKeys = {
  all: ['puntaje'],
  dashboard: (filters) => [...puntajeKeys.all, 'dashboard', filters],
  ranking: (groupUuid, filters) => [...puntajeKeys.all, 'ranking', groupUuid, filters],
  eligibility: (groupUuid, filters) => [...puntajeKeys.all, 'eligibility', groupUuid, filters],
  fundDetails: (groupUuid, fundType, filters) => [
    ...puntajeKeys.all,
    'fund-details',
    groupUuid,
    fundType,
    filters,
  ],
  preAdjudications: (groupUuid, filters) => [
    ...puntajeKeys.all,
    'pre-adjudications',
    groupUuid,
    filters,
  ],
  contractDebtDetail: (contractUuid) => [...puntajeKeys.all, 'contract-debt-detail', contractUuid],
  couponDetail: (couponUuid) => [...puntajeKeys.all, 'coupon-detail', couponUuid],
  contractDetail: (contractUuid) => [...puntajeKeys.all, 'contract-detail', contractUuid],
  lotteryResults: (filters) => [...puntajeKeys.all, 'lottery-results', filters],
  initialBalances: (filters) => [...puntajeKeys.all, 'initial-balances', filters],
};

const buildParams = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

export const usePuntajeDashboard = (filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.dashboard(filters),
    queryFn: async () => {
      const response = await apiClient.get('puntaje/dashboard', {
        params: buildParams(filters),
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeRanking = (groupUuid, filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.ranking(groupUuid, filters),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/groups/${groupUuid}/ranking`, {
        params: buildParams(filters),
      });
      return response.data;
    },
    enabled: !!groupUuid,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeEligibility = (groupUuid, filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.eligibility(groupUuid, filters),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/groups/${groupUuid}/eligibility`, {
        params: buildParams(filters),
      });
      return response.data;
    },
    enabled: !!groupUuid,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeFundDetails = (groupUuid, fundType, filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.fundDetails(groupUuid, fundType, filters),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/groups/${groupUuid}/funds/${fundType}/movements`, {
        params: buildParams(filters),
      });
      return response.data;
    },
    enabled: !!groupUuid && !!fundType,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajePreAdjudications = (groupUuid, filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.preAdjudications(groupUuid, filters),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/groups/${groupUuid}/pre-adjudications`, {
        params: buildParams(filters),
      });
      return response.data;
    },
    enabled: !!groupUuid,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeContractDebtDetail = (contractUuid, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.contractDebtDetail(contractUuid),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/contracts/${contractUuid}/debt-detail`);
      return response.data;
    },
    enabled: !!contractUuid,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeCouponDetail = (couponUuid, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.couponDetail(couponUuid),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/coupons/${couponUuid}/detail`);
      return response.data;
    },
    enabled: !!couponUuid,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeContractDetail = (contractUuid, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.contractDetail(contractUuid),
    queryFn: async () => {
      const response = await apiClient.get(`puntaje/contracts/${contractUuid}/detail`);
      return response.data;
    },
    enabled: !!contractUuid,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeLotteryResults = (filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.lotteryResults(filters),
    queryFn: async () => {
      const response = await apiClient.get('puntaje/lottery-results', {
        params: buildParams(filters),
      });
      return response.data;
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const usePuntajeInitialBalances = (filters = {}, options = {}) =>
  useQuery({
    queryKey: puntajeKeys.initialBalances(filters),
    queryFn: async () => {
      const response = await apiClient.get('puntaje/initial-balances', {
        params: buildParams(filters),
      });
      return response.data;
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });

export const useConfirmPuntajePreAdjudication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('puntaje/pre-adjudications/confirm', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: puntajeKeys.all });
    },
    onError: (error) => {
      console.error('Error confirming puntaje pre-adjudication:', error);
    },
  });
};

export const useUpsertPuntajeLotteryResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post('puntaje/lottery-results', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: puntajeKeys.all });
    },
    onError: (error) => {
      console.error('Error upserting puntaje lottery result:', error);
    },
  });
};

export const useSyncPuntajeLotteryResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filters = {}) => {
      const response = await apiClient.post('puntaje/lottery-results/sync', null, {
        params: buildParams(filters),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: puntajeKeys.all });
    },
    onError: (error) => {
      console.error('Error syncing puntaje lottery result:', error);
    },
  });
};

export const useUpsertPuntajeInitialBalance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.put('puntaje/initial-balances', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: puntajeKeys.all });
    },
    onError: (error) => {
      console.error('Error upserting puntaje initial balance:', error);
    },
  });
};

export const useDeletePuntajeInitialBalance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (initialBalanceUuid) => {
      const response = await apiClient.delete(`puntaje/initial-balances/${initialBalanceUuid}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: puntajeKeys.all });
    },
    onError: (error) => {
      console.error('Error deleting puntaje initial balance:', error);
    },
  });
};
